import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Clock, CheckCircle2, FileSignature, AlertTriangle, Loader2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { comisionesApi } from '../../lib/api';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';

const formatMoney = (monto, moneda = 'ARS') => {
  const n = Number(monto || 0);
  const simbolo = moneda === 'USD' ? 'US$' : '$';
  return `${simbolo} ${new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n)}`;
};

const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const formatMes = (ym) => {
  const [y, m] = ym.split('-');
  return `${MESES_CORTOS[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

// Gráfico de líneas SVG para la serie mensual
const LineChart = ({ serie }) => {
  const width = 900;
  const height = 280;
  const padding = { top: 20, right: 24, bottom: 36, left: 70 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const maxY = Math.max(1, ...serie.map(d => d.monto));
  const avgY = serie.length > 0 ? serie.reduce((s, d) => s + d.monto, 0) / serie.length : 0;

  const xStep = serie.length > 1 ? innerW / (serie.length - 1) : 0;
  const yScale = (v) => innerH - (v / maxY) * innerH;

  const points = serie.map((d, i) => ({
    x: padding.left + i * xStep,
    y: padding.top + yScale(d.monto),
    ...d,
  }));

  const pathD = points.map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`)).join(' ');
  const areaD = points.length
    ? `${pathD} L ${points[points.length - 1].x},${padding.top + innerH} L ${points[0].x},${padding.top + innerH} Z`
    : '';

  // Y axis ticks (4)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(p => ({
    value: maxY * p,
    y: padding.top + innerH - p * innerH,
  }));

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" preserveAspectRatio="none">
      <defs>
        <linearGradient id="comision-gradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1B4D3E" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#1B4D3E" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Y grid + labels */}
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={padding.left} y1={t.y} x2={width - padding.right} y2={t.y} stroke="#e2e8f0" strokeDasharray="2 3" />
          <text x={padding.left - 8} y={t.y + 4} textAnchor="end" fontSize="10" fill="#94a3b8">
            {formatMoney(t.value)}
          </text>
        </g>
      ))}

      {/* Línea de promedio */}
      {avgY > 0 && (
        <g>
          <line
            x1={padding.left}
            y1={padding.top + yScale(avgY)}
            x2={width - padding.right}
            y2={padding.top + yScale(avgY)}
            stroke="#f59e0b"
            strokeWidth="1.2"
            strokeDasharray="5 4"
          />
          <text
            x={width - padding.right - 4}
            y={padding.top + yScale(avgY) - 4}
            textAnchor="end"
            fontSize="10"
            fill="#b45309"
            fontWeight="600"
          >
            Prom. {formatMoney(avgY)}
          </text>
        </g>
      )}

      {/* Área */}
      {areaD && <path d={areaD} fill="url(#comision-gradient)" />}

      {/* Línea */}
      {pathD && <path d={pathD} fill="none" stroke="#1B4D3E" strokeWidth="2.5" strokeLinejoin="round" />}

      {/* Puntos + tooltips */}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4" fill="#fff" stroke="#1B4D3E" strokeWidth="2" />
          <title>{`${formatMes(p.mes)}: ${formatMoney(p.monto)} (${p.count} contratos)`}</title>
        </g>
      ))}

      {/* X labels */}
      {points.map((p, i) => (
        <text
          key={i}
          x={p.x}
          y={height - padding.bottom + 16}
          textAnchor="middle"
          fontSize="10"
          fill="#64748b"
        >
          {formatMes(p.mes)}
        </text>
      ))}
    </svg>
  );
};

export const AnalistaReporteComisionesPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [promedioRango, setPromedioRango] = useState('3');

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await comisionesApi.reporte({ meses: 12 });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar reporte');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const promedioMonto = data?.promedios?.[promedioRango] ?? 0;
  const formatoFecha = (s) => {
    if (!s) return '-';
    try { return new Date(s + 'T00:00:00').toLocaleDateString('es-AR'); } catch { return s; }
  };

  // Mejor mes del período (opcional visual sutil)
  const mejorMes = useMemo(() => {
    if (!data?.serie?.length) return null;
    return data.serie.reduce((best, c) => (c.monto > (best?.monto || 0) ? c : best), null);
  }, [data]);

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-emerald-700" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Reporte</h1>
          <p className="text-sm text-slate-500">Comisiones devengadas · últimos 12 meses</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : !data ? (
        <div className="text-center text-slate-500 py-10">Sin datos para mostrar.</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            {/* Promedio */}
            <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="kpi-promedio">
              <div className="flex items-center justify-between mb-1">
                <span className="flex items-center gap-2 text-slate-500 text-xs">
                  <TrendingUp className="w-4 h-4" />
                  Promedio mensual
                </span>
                <Select value={promedioRango} onValueChange={setPromedioRango}>
                  <SelectTrigger className="h-7 w-[88px] text-xs" data-testid="select-promedio-rango"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Último mes</SelectItem>
                    <SelectItem value="3">3 meses</SelectItem>
                    <SelectItem value="6">6 meses</SelectItem>
                    <SelectItem value="12">12 meses</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-2xl font-semibold text-slate-900 mt-1">{formatMoney(promedioMonto)}</div>
              <div className="text-xs text-slate-400 mt-1">
                Últimos {promedioRango} {promedioRango === '1' ? 'mes' : 'meses'}
              </div>
            </div>

            {/* Pendientes */}
            <div className="bg-white border border-amber-200 rounded-lg p-4" data-testid="kpi-pendientes">
              <div className="flex items-center gap-2 text-amber-700 text-xs mb-1">
                <Clock className="w-4 h-4" />
                Pendientes de cobro
              </div>
              <div className="text-2xl font-semibold text-amber-800">{formatMoney(data.pendientes.monto)}</div>
              <div className="text-xs text-slate-500 mt-1">{data.pendientes.count} comisiones</div>
            </div>

            {/* Cobrado período */}
            <div className="bg-white border border-green-200 rounded-lg p-4" data-testid="kpi-cobrado">
              <div className="flex items-center gap-2 text-green-700 text-xs mb-1">
                <CheckCircle2 className="w-4 h-4" />
                Cobrado (12m)
              </div>
              <div className="text-2xl font-semibold text-green-800">{formatMoney(data.cobradas.monto_periodo)}</div>
              <div className="text-xs text-slate-500 mt-1">{data.cobradas.count_periodo} comisiones cobradas</div>
            </div>

            {/* Contratos firmados */}
            <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="kpi-contratos">
              <div className="flex items-center gap-2 text-slate-600 text-xs mb-1">
                <FileSignature className="w-4 h-4" />
                Contratos firmados
              </div>
              <div className="text-2xl font-semibold text-slate-900">{data.firmados_periodo}</div>
              <div className="text-xs text-slate-400 mt-1">
                últimos 12 meses
                {mejorMes && mejorMes.monto > 0 && <> · mejor mes <span className="font-medium text-slate-600">{formatMes(mejorMes.mes)}</span></>}
              </div>
            </div>
          </div>

          {/* Grid: Gráfico + Próximas pendientes */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Gráfico */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-700">Comisión devengada por mes</h3>
                <span className="text-xs text-slate-400">basado en fecha de firma</span>
              </div>
              <LineChart serie={data.serie} />
            </div>

            {/* Próximas a cobrar */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  Próximas a cobrar
                </h3>
                <Link to="/comercial/comisiones" className="text-xs text-emerald-700 hover:underline">Ver todas →</Link>
              </div>
              {data.proximas_pendientes.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">
                  No hay pendientes. ¡Bien ahí!
                </p>
              ) : (
                <div className="space-y-2">
                  {data.proximas_pendientes.map((p) => (
                    <div
                      key={p.comision_id}
                      className="border border-slate-100 rounded-md px-3 py-2 hover:bg-slate-50"
                      data-testid={`proxima-${p.comision_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">
                            {p.inquilino || '—'}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {p.edificio_nombre || '—'} · {p.unidad_nombre || '—'}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatoFecha(p.fecha_firma)}
                            {p.dias_desde_firma != null && (
                              <span className="ml-1 text-amber-600 font-medium">
                                · {p.dias_desde_firma} días
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <div className="text-sm font-semibold text-slate-900">
                            {formatMoney(p.monto_comision, p.moneda)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalistaReporteComisionesPage;
